import type { IRLFieldKey, IRLRequest, IRLMeta } from "../types";

const REQUESTS_KEY = "__irlRequests";
const META_KEY = "__irlMeta";
// Per-project list of folder codes (any depth — "1", "1.3", "9") that the IRL
// matcher should SKIP. Empty / missing entry = no exclusions = match every
// file. Storing excluded codes (rather than an allowlist) lets the legacy
// "match all" default work without any migration.
const EXCLUDED_FOLDERS_KEY = "__irlMatchExcludedFolders";
// Per-project flag: has the user ever interacted with the folder picker for
// this project? Used to distinguish "fresh project, seed all-excluded so the
// matcher is opt-in" from "user explicitly cleared the exclusion list to match
// everything." Without this, both states would be an empty exclusion list.
const SELECTION_INIT_KEY = "__irlMatchSelectionInitialized";
// Per-project flag for whether the "Folders to match" picker is expanded in
// the IRL tab. Defaults open the first time so the user discovers it; once
// they collapse it, we remember.
const PANEL_OPEN_KEY = "__irlMatchPanelOpen";

type RequestsMap = Record<string, IRLRequest[]>;
type MetaMap = Record<string, IRLMeta>;
type ExcludedFoldersMap = Record<string, string[]>;
type SelectionInitMap = Record<string, boolean>;
type PanelOpenMap = Record<string, boolean>;

function readRequests(): RequestsMap {
  try {
    const raw = localStorage.getItem(REQUESTS_KEY);
    return raw ? (JSON.parse(raw) as RequestsMap) : {};
  } catch {
    return {};
  }
}

function writeRequests(map: RequestsMap): void {
  try {
    localStorage.setItem(REQUESTS_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

function readPanelOpen(): PanelOpenMap {
  try {
    const raw = localStorage.getItem(PANEL_OPEN_KEY);
    return raw ? (JSON.parse(raw) as PanelOpenMap) : {};
  } catch {
    return {};
  }
}

function writePanelOpen(map: PanelOpenMap): void {
  try {
    localStorage.setItem(PANEL_OPEN_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

function readExcludedFolders(): ExcludedFoldersMap {
  try {
    const raw = localStorage.getItem(EXCLUDED_FOLDERS_KEY);
    return raw ? (JSON.parse(raw) as ExcludedFoldersMap) : {};
  } catch {
    return {};
  }
}

function writeExcludedFolders(map: ExcludedFoldersMap): void {
  try {
    localStorage.setItem(EXCLUDED_FOLDERS_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

function readSelectionInit(): SelectionInitMap {
  try {
    const raw = localStorage.getItem(SELECTION_INIT_KEY);
    return raw ? (JSON.parse(raw) as SelectionInitMap) : {};
  } catch {
    return {};
  }
}

function writeSelectionInit(map: SelectionInitMap): void {
  try {
    localStorage.setItem(SELECTION_INIT_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

function readMeta(): MetaMap {
  try {
    const raw = localStorage.getItem(META_KEY);
    return raw ? (JSON.parse(raw) as MetaMap) : {};
  } catch {
    return {};
  }
}

function writeMeta(map: MetaMap): void {
  try {
    localStorage.setItem(META_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

export function getIRLRequests(projectId: string): IRLRequest[] {
  return readRequests()[projectId] || [];
}

export function getIRLMeta(projectId: string): IRLMeta | null {
  return readMeta()[projectId] ?? null;
}

export function saveIRLRequests(
  projectId: string,
  requests: IRLRequest[],
  originalFileName: string,
  uploadCapture?: {
    headers: string[];
    mapping: (IRLFieldKey | "")[];
    /** Base64-encoded original file bytes (for round-trip Export). */
    fileBytes?: string;
    headerRowIdx?: number;
    lastDataRowIdx?: number;
    importedRowIndices?: number[];
  },
): IRLMeta {
  const map = readRequests();
  map[projectId] = requests;
  writeRequests(map);

  // Edits after the initial upload should keep the original column layout
  // AND original file bytes so Export still round-trips into the source
  // format. We only overwrite when the caller explicitly passes new
  // upload-capture data (i.e., the upload-mapping flow); plain row edits
  // leave it intact.
  const metaMap = readMeta();
  const existing = metaMap[projectId];
  const meta: IRLMeta = {
    uploadedAt: new Date().toISOString(),
    originalFileName,
    count: requests.length,
    originalHeaders: uploadCapture?.headers ?? existing?.originalHeaders,
    originalMapping: uploadCapture?.mapping ?? existing?.originalMapping,
    originalFileBytes: uploadCapture?.fileBytes ?? existing?.originalFileBytes,
    originalHeaderRowIdx:
      uploadCapture?.headerRowIdx ?? existing?.originalHeaderRowIdx,
    originalLastDataRowIdx:
      uploadCapture?.lastDataRowIdx ?? existing?.originalLastDataRowIdx,
    originalImportedRowIndices:
      uploadCapture?.importedRowIndices ?? existing?.originalImportedRowIndices,
  };
  metaMap[projectId] = meta;
  writeMeta(metaMap);

  return meta;
}

export function clearIRLRequests(projectId: string): void {
  const map = readRequests();
  delete map[projectId];
  writeRequests(map);

  const metaMap = readMeta();
  delete metaMap[projectId];
  writeMeta(metaMap);

  // Folder exclusions + the "selection initialized" flag are tied to this
  // project's IRL list; clear both so a re-upload starts fresh (seeded to
  // "nothing selected") rather than inheriting stale exclusions.
  const excluded = readExcludedFolders();
  if (excluded[projectId]) {
    delete excluded[projectId];
    writeExcludedFolders(excluded);
  }
  const selectionInit = readSelectionInit();
  if (selectionInit[projectId]) {
    delete selectionInit[projectId];
    writeSelectionInit(selectionInit);
  }

  const panel = readPanelOpen();
  if (projectId in panel) {
    delete panel[projectId];
    writePanelOpen(panel);
  }
}

/**
 * Top-level folder codes (e.g., "9", "10") that the IRL matcher should SKIP
 * for this project. Empty array = no exclusions = match every file.
 *
 * NOTE: a fresh project that has never been touched will return [] here too.
 * Use `isIRLMatchSelectionInitialized()` to distinguish "fresh — seed
 * everything excluded so matching is opt-in" from "user explicitly chose to
 * match all folders."
 */
export function getIRLMatchExcludedFolders(projectId: string): string[] {
  return readExcludedFolders()[projectId] || [];
}

export function saveIRLMatchExcludedFolders(projectId: string, codes: string[]): void {
  const map = readExcludedFolders();
  if (codes.length === 0) {
    delete map[projectId];
  } else {
    // De-dup + sort so storage is canonical regardless of click order.
    map[projectId] = Array.from(new Set(codes)).sort();
  }
  writeExcludedFolders(map);
  // Any save means the user has interacted with the picker — flag the
  // selection as initialized so the panel doesn't re-seed on next mount.
  markIRLMatchSelectionInitialized(projectId);
}

/**
 * Has the user ever interacted with the folder picker for this project?
 * False until the first toggle / select-all / clear-all click. Drives the
 * one-time "seed everything excluded" default on fresh projects.
 */
export function isIRLMatchSelectionInitialized(projectId: string): boolean {
  return Boolean(readSelectionInit()[projectId]);
}

export function markIRLMatchSelectionInitialized(projectId: string): void {
  const map = readSelectionInit();
  if (map[projectId]) return;
  map[projectId] = true;
  writeSelectionInit(map);
}

/**
 * Whether the IRL-tab folder picker panel is expanded for this project.
 * Defaults to true the first time so the user discovers it.
 */
export function getIRLMatchPanelOpen(projectId: string): boolean {
  const map = readPanelOpen();
  return projectId in map ? map[projectId] : true;
}

export function saveIRLMatchPanelOpen(projectId: string, open: boolean): void {
  const map = readPanelOpen();
  map[projectId] = open;
  writePanelOpen(map);
}
